import * as angular from "angular";
import type {
	IPromise,
	IQService,
	IWindowService
} from "angular";

export type GpsPermissionState = "denied" | "granted" | "prompt" | "unknown" | "unsupported";
export type GpsErrorCode = "permission-denied" | "position-unavailable" | "unsupported";

export interface GpsCoordinates {
	latitude: number;
	longitude: number;
}

export class GpsError extends Error {
	constructor(public readonly code: GpsErrorCode, message: string) {
		super(message);
		this.name = "GpsError";
	}
}

export class GpsService {
	constructor(
		private readonly $q: IQService,
		private readonly $window: IWindowService
	) {
	}

	isSupported(): boolean {
		return typeof this.$window.navigator?.geolocation?.getCurrentPosition === "function";
	}

	getPermissionState(): IPromise<GpsPermissionState> {
		if(!this.isSupported()) {
			return this.$q.resolve("unsupported");
		}

		const permissions = this.$window.navigator.permissions;
		if(typeof permissions?.query !== "function") {
			return this.$q.resolve("unknown");
		}

		return this.$q.when(undefined)
			.then(() => permissions.query({ name: "geolocation" }))
			.then(status => status.state)
			.catch(() => "unknown");
	}

	getCurrentPosition(): IPromise<GpsCoordinates> {
		if(!this.isSupported()) {
			return this.$q.reject(new GpsError("unsupported", "Current position is not supported by this browser."));
		}

		return this.getPermissionState().then(permissionState => {
			if(permissionState === "denied") {
				return this.$q.reject(new GpsError("permission-denied", "Current position permission was previously denied."));
			}

			const deferred = this.$q.defer<GpsCoordinates>();
			this.$window.navigator.geolocation.getCurrentPosition(
				position => deferred.resolve({
					latitude: position.coords.latitude,
					longitude: position.coords.longitude
				}),
				error => deferred.reject(new GpsError(
					error.code === error.PERMISSION_DENIED ? "permission-denied" : "position-unavailable",
					error.message || "Current position is unavailable."
				))
			);

			return deferred.promise;
		});
	}
}

export default angular
	.module("vesl.services.gps-service", [])
	.service("gpsService", GpsService).name;
